import { bsClassJustifyContent } from '@common/types/bootstrap';
import classNames from 'classnames';
import { isEmpty } from 'lodash';
import React, { PropsWithChildren } from 'react';
import { Form, Row, Col } from 'react-bootstrap';
import { LabelPosition } from '@common/models/Enums/LabelPosition';

interface IFormGroupProps {
  controlId: string;
  controlLabel: string;
  reverseLabel?: boolean;
  controlLabelRowJustifyClass?: bsClassJustifyContent;
  controlLabelTailElement?: JSX.Element;
  helpId?: string;
  helpText?: string;
  errorId: string;
  errorMessage?: string;
  className?: string;
  labelPosition?: LabelPosition;
  /**
   * Based on the BootStrap grid system, this value define the
   * width of the the column where the label will be positioned
   * on small breackpoint.
   * The value will be used when the LabelPosition is defined
   * as LabelPosition.LEFT.
   */
  labelColumnSmWidth?: number;
  /**
   * Based on the BootStrap grid system, this value define the
   * width of the the column where the input will be positioned
   * on small breackpoint.
   * The value will be used when the LabelPosition is defined
   * as LabelPosition.LEFT.
   */
  inputColumnSmWidth?: number;
  /**
   * Based on the BootStrap grid system, this value define the
   * width of the the column where the label will be positioned
   * on medium breackpoint.
   * The value will be used when the LabelPosition is defined
   * as LabelPosition.LEFT.
   */
  labelColumnMdWidth?: number;
  /**
   * Based on the BootStrap grid system, this value define the
   * width of the the column where the input will be positioned
   * on medium breackpoint.
   * The value will be used when the LabelPosition is defined
   * as LabelPosition.LEFT.
   */
  inputColumnMdWidth?: number;
  /**
   * Class(es) name(s) for the column where the input will be placed.
   */
  inputColumnClassName?: string;
  /**
   * CSS class for the flex display style applied to the label container.
   * Typically "d-flex" (block flex) or "d-inline-flex" (inline flex).
   * Defaults to "d-flex".
   */
  flexDisplayType?: string;
  /**
   * CSS class for the align-items style applied to the label container.
   * e.g. "align-items-center", "align-items-end".
   * Defaults to empty string (no override).
   */
  labelAlignClass?: string;
}

const RenderLabel = (
  controlId: string,
  label: string,
  justifyClass: bsClassJustifyContent,
  flexDisplayType: string,
  labelPosition: LabelPosition,
  labelColumnSmWidth?: number,
  labelColumnMdWidth?: number,
  controlLabelTailElement?: JSX.Element,
  labelAlignClass?: string,
): JSX.Element => {
  if (controlLabelTailElement) {
    return (
      <div className={classNames(flexDisplayType, 'flex-row', justifyClass, labelAlignClass)}>
        {!isEmpty(label) && (
          <Form.Label htmlFor={controlId}>{label}</Form.Label>
        )}
        {controlLabelTailElement}
      </div>
    );
  } else if (isEmpty(label)) {
    return <></>;
  } else {
    return RenderLabelPosition(
      controlId,
      label,
      labelPosition,
      labelColumnSmWidth,
      labelColumnMdWidth,
    );
  }
};

const RenderLabelPosition = (
  controlId: string,
  label: string,
  labelPosition: LabelPosition,
  labelColumnSmWidth?: number,
  labelColumnMdWidth?: number,
): JSX.Element => {
  let labelToRender = <></>;

  switch (labelPosition) {
    case LabelPosition.TOP:
      labelToRender = <Form.Label htmlFor={controlId}>{label}</Form.Label>;
      break;

    case LabelPosition.RIGHT:
      labelToRender = (
        <Col
          sm={labelColumnSmWidth}
          md={labelColumnMdWidth}
          className="d-flex align-items-center"
        >
          <Form.Label htmlFor={controlId} style={{ fontSize: '1rem' }}>
            {label}
          </Form.Label>
        </Col>
      );
      break;

    case LabelPosition.LEFT:
      labelToRender = (
        <Col
          sm={labelColumnSmWidth}
          md={labelColumnMdWidth}
          className="d-flex align-items-center"
        >
          <Form.Label htmlFor={controlId}>{label}</Form.Label>
        </Col>
      );
      break;
    default:
      labelToRender = <></>;
      break;
  }

  return labelToRender;
};

export const FormGroup = ({
  controlLabelRowJustifyClass = 'justify-content-between',
  ...props
}: PropsWithChildren<IFormGroupProps>) => {
  const {
    controlId,
    controlLabel,
    labelPosition = LabelPosition.TOP,
    controlLabelTailElement,
    labelColumnSmWidth,
    inputColumnSmWidth,
    labelColumnMdWidth,
    inputColumnMdWidth,
    inputColumnClassName = '',
    flexDisplayType = 'd-flex',
    labelAlignClass = '',
  } = props;
  let formGroup;

  switch (labelPosition) {
    case LabelPosition.LEFT:
      formGroup = (
        <Form.Group data-testid={props.controlId} className={props.className}>
          <Row className="mt-3">
            {RenderLabel(
              controlId,
              controlLabel,
              controlLabelRowJustifyClass,
              flexDisplayType,
              labelPosition as LabelPosition,
              labelColumnSmWidth,
              labelColumnMdWidth,
              controlLabelTailElement,
              labelAlignClass,
            )}
            <Col
              className={inputColumnClassName}
              sm={inputColumnSmWidth}
              md={inputColumnMdWidth}
            >
              {props.children}
              {props.errorMessage && <div className="is-invalid"></div>}
              <Form.Control.Feedback type="invalid">
                {props.errorMessage && (
                  <span role="alert" id={props.errorId}>
                    {props.errorMessage}
                  </span>
                )}
              </Form.Control.Feedback>
            </Col>
          </Row>
        </Form.Group>
      );
      break;

    case LabelPosition.RIGHT:
      formGroup = (
        <Form.Group data-testid={controlId} className={props.className}>
          <Row className="mt-3 gx-1">
            <Col
              className={inputColumnClassName}
              sm={inputColumnSmWidth}
              md={inputColumnMdWidth}
            >
              {props.children}
            </Col>
            <Col
              sm={labelColumnSmWidth}
              md={labelColumnMdWidth}
              className="d-flex align-items-end justify-content-end"
            >
              {RenderLabel(
                controlId,
                controlLabel,
                controlLabelRowJustifyClass,
                flexDisplayType,
                labelPosition,
                labelColumnSmWidth,
                labelColumnMdWidth,
                controlLabelTailElement,
                labelAlignClass,
              )}
            </Col>
          </Row>
          <Row>
            <Col sm={{ span: inputColumnSmWidth, offset: labelColumnSmWidth }}>
              {props.errorMessage && <div className="is-invalid"></div>}
              <Form.Control.Feedback type="invalid">
                {props.errorMessage && (
                  <span role="alert" id={props.errorId}>
                    {props.errorMessage}
                  </span>
                )}
              </Form.Control.Feedback>
            </Col>
          </Row>
        </Form.Group>
      );
      break;

    case LabelPosition.TOP:
      formGroup = (
        <Form.Group data-testid={props.controlId} className={props.className}>
          {!props.reverseLabel &&
            RenderLabel(
              props.controlId,
              props.controlLabel,
              controlLabelRowJustifyClass,
              flexDisplayType,
              labelPosition as LabelPosition,
              labelColumnSmWidth,
              labelColumnMdWidth,
              props.controlLabelTailElement,
              labelAlignClass,
            )}
          {props.children}
          {props.reverseLabel &&
            RenderLabel(
              props.controlId,
              props.controlLabel,
              controlLabelRowJustifyClass,
              flexDisplayType,
              labelPosition as LabelPosition,
              labelColumnSmWidth,
              labelColumnMdWidth,
              props.controlLabelTailElement,
              labelAlignClass,
            )}
          {props.helpText && (
            <Form.Text id={props.helpId}>{props.helpText}</Form.Text>
          )}
          <Form.Control.Feedback type="invalid">
            {props.errorMessage && (
              <span role="alert" id={props.errorId}>
                {props.errorMessage}
              </span>
            )}
          </Form.Control.Feedback>
        </Form.Group>
      );
      break;
  }

  return formGroup;
};
